/**
 * Settings — workspace metadata + team list.
 *
 * Workspace and team are split into separate queries because the user
 * spends most of their time on Workspace (one tab) and we don't want
 * Team's row count to invalidate workspace updates.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';

export interface Workspace {
  id: number;
  name: string;
  type: 'platform' | 'reseller' | 'customer';
  status: 'active' | 'suspended' | 'closed';
  domain?: string;
  support_email?: string;
  legal_name?: string;
  country?: string;
  timezone?: string;
  created_at: string;
}

export interface UpdateWorkspaceInput {
  /** Send only the fields you want changed. Empty string clears the field. */
  name?: string;
  support_email?: string;
  legal_name?: string;
  country?: string;
  timezone?: string;
}

export type UserRole = 'owner' | 'admin' | 'operator' | 'viewer' | 'api';

export interface TeamMember {
  id: number;
  email: string;
  full_name?: string;
  role: UserRole;
  is_active: boolean;
  last_login_at?: string;
  created_at: string;
}

export interface TeamResponse {
  items: TeamMember[];
  total: number;
}

const SETTINGS_KEY = ['settings'] as const;

export function useWorkspace() {
  return useQuery({
    queryKey: [...SETTINGS_KEY, 'workspace'],
    queryFn: () => api.get<Workspace>('/v1/settings/workspace'),
    staleTime: 60_000,
  });
}

export function useUpdateWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateWorkspaceInput) =>
      api.patch<Workspace>('/v1/settings/workspace', input),
    onSuccess: (data) => {
      qc.setQueryData([...SETTINGS_KEY, 'workspace'], data);
    },
  });
}

export function useTeam() {
  return useQuery({
    queryKey: [...SETTINGS_KEY, 'team'],
    queryFn: () => api.get<TeamResponse>('/v1/settings/team'),
    staleTime: 60_000,
  });
}

export interface InviteTeamMemberInput {
  email: string;
  full_name: string;
  role: 'admin' | 'operator' | 'viewer';
}

/**
 * POST /v1/settings/team — invites a new user. Backend creates a
 * passwordless user, mints a one-time link, and emails it. Recipient
 * lands on the same /reset-password/:token page used for forgot-pw,
 * sets their password, and is signed in.
 *
 * Invalidates the team list on success so the new pending member
 * appears immediately.
 */
export function useInviteTeamMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: InviteTeamMemberInput) =>
      api.post<TeamMember>('/v1/settings/team', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...SETTINGS_KEY, 'team'] });
    },
  });
}

export interface UpdateTeamMemberInput {
  id: number;
  /** Send role to change role, is_active to flip enable. Both optional. */
  role?: 'admin' | 'operator' | 'viewer';
  is_active?: boolean;
}

/** PATCH /v1/settings/team/{id} — change role and/or active flag. */
export function useUpdateTeamMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateTeamMemberInput) =>
      api.patch<TeamMember>(`/v1/settings/team/${id}`, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...SETTINGS_KEY, 'team'] });
    },
  });
}

/**
 * DELETE /v1/settings/team/{id}. Backend rejects with 409 in_use if
 * the user has historical references (transactions, audit, etc) — UI
 * should offer Deactivate as the fallback path.
 */
export function useDeleteTeamMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/v1/settings/team/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...SETTINGS_KEY, 'team'] });
    },
  });
}

/** POST /v1/settings/team/{id}/resend-invite — re-mints the invite link. */
export function useResendTeamInvitation() {
  return useMutation({
    mutationFn: (id: number) => api.post<void>(`/v1/settings/team/${id}/resend-invite`),
  });
}
